import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, CreditCard, ChevronRight, XCircle, AlarmClock, AlertTriangle, Package } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useCookieConsent, safeSetItem, safeGetItem } from "../contexts/CookieConsentContext";

const REJECT_DISMISS_KEY = "apex_seen_rejected_v1";
const PAY_DISMISS_KEY = "apex_seen_pay_v1";
const PKG_VISIT_KEY_PREFIX = "apex_pkg_visits_v1_";

// Dismiss state is functional-category data — load via safeGetItem so it
// respects the cookie consent. If the user only accepted essentials, every
// notification will simply reappear on each page reload (intentional).
function loadSeen(key) {
  try {
    const raw = safeGetItem(key, null, "functional");
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
}
function saveSeen(key, ids) {
  // safeSetItem silently skips when functional consent is not given
  safeSetItem(key, JSON.stringify(Array.from(ids)), "functional");
}

// Package-offer "nudge" visit counter — once the customer has dismissed the
// offer for the first time, it goes quiet and resurfaces every 3rd page
// visit thereafter until they open it again (which resets the counter).
function getPkgVisitCount(userId) {
  try {
    const raw = safeGetItem(PKG_VISIT_KEY_PREFIX + userId, "0", "functional");
    return parseInt(raw, 10) || 0;
  } catch { return 0; }
}
function setPkgVisitCount(userId, n) {
  safeSetItem(PKG_VISIT_KEY_PREFIX + userId, String(n), "functional");
}
function shouldShowPackageNudge(userId) {
  const n = getPkgVisitCount(userId);
  return n > 0 && n % 3 === 0;
}

// "Service is starting within 24h" check. Bookings that are this close trigger
// the auto-payment-reminder cron + a more urgent UI in the bell.
function isImminent(b) {
  if (!b?.date || !b?.time) return false;
  const dt = new Date(`${b.date}T${b.time}:00`);
  if (Number.isNaN(dt.getTime())) return false;
  const ms = dt.getTime() - Date.now();
  return ms > 0 && ms <= 24 * 60 * 60 * 1000;
}

// Customer notification bell — surfaces actionable items:
//  • approved online booking waiting for payment ("pay" CTA)
//    – marked URGENT when the auto-reminder cron flagged it OR the service
//      starts within 24h
//  • recently rejected bookings (dismissable once seen)
export const NotificationBell = ({ onOpenDashboard }) => {
  const { user, setUser } = useAuth();
  const { consent } = useCookieConsent();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const buildItems = useCallback((bookings) => {
    const seenRejected = loadSeen(REJECT_DISMISS_KEY);
    const seenPay = loadSeen(PAY_DISMISS_KEY);
    const list = [];

    // Package offer notification — shown on the first visit after completing
    // the first detailing (backend sets user.packageOfferAt) and stays until
    // dismissed (user.packageOfferSeen). After that first dismissal it goes
    // quiet and resurfaces every 3rd subsequent page visit as a gentle nudge.
    if (user?.packageOfferAt) {
      const uid = user.id || user._id;
      if (!user?.packageOfferSeen) {
        list.push({ type: "package_offer", id: "package-offer" });
      } else if (uid && shouldShowPackageNudge(uid)) {
        list.push({ type: "package_offer", id: "package-offer" });
      }
    }

    for (const b of bookings) {
      const id = b.id || b._id;
      if (b.status === "confirmed" && b.paymentMethod !== "cash" && b.paymentStatus !== "paid") {
        const urgent = Boolean(b.autoReminderSentAt) || isImminent(b);
        // Urgent items override the dismiss so the user can't accidentally
        // silence a same-day reminder.
        if (!urgent && seenPay.has(id)) continue;
        list.push({ type: "pay", id, booking: b, urgent });
      } else if (b.status === "cancelled" && b.rejectionReason === "rejected_by_staff" && !seenRejected.has(id)) {
        list.push({ type: "rejected", id, booking: b });
      }
    }
    // Urgent payment reminders bubble to the top
    list.sort((a, b) => Number(Boolean(b.urgent)) - Number(Boolean(a.urgent)));
    return list;
  }, [user]);

  const load = useCallback(() => {
    if (!user || user.role !== "customer") return;
    api.get("/bookings/my-bookings")
      .then((r) => setItems(buildItems(r.data || [])))
      .catch(() => {});
  }, [user, buildItems]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
    // Re-run when consent.functional flips so dismissed-items state refreshes
    // immediately (e.g., user revokes functional → all dismissed items return).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, consent?.functional]);

  // Count this page load as one "visit" toward the recurring package-offer
  // nudge — but only once the offer has already been dismissed at least once
  // (packageOfferSeen), and only once per mount (not on every 60s poll).
  useEffect(() => {
    const uid = user?.id || user?._id;
    if (!uid || !user?.packageOfferAt || !user?.packageOfferSeen) return;
    setPkgVisitCount(uid, getPkgVisitCount(uid) + 1);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?._id, user?.packageOfferAt, user?.packageOfferSeen]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const dismissRejected = (id, e) => {
    e?.stopPropagation();
    const seen = loadSeen(REJECT_DISMISS_KEY);
    seen.add(id);
    saveSeen(REJECT_DISMISS_KEY, seen);
    setItems((prev) => prev.filter((it) => !(it.type === "rejected" && it.id === id)));
  };

  // Generic "I've seen it" handler used when the user clicks a row to open
  // the dashboard. Removes the item from the bell and persists the dismiss.
  const dismissOnOpen = (it) => {
    if (it.type === "package_offer") {
      // Persist via backend so it's truly one-time across devices/sessions,
      // and also flip local state so the bell updates immediately without
      // waiting for the next /me refresh.
      api.post("/auth/me/dismiss-package-offer").catch(() => {});
      setUser?.((u) => (u ? { ...u, packageOfferSeen: true } : u));
      // Restart the "every 3rd visit" nudge cycle from zero.
      const uid = user?.id || user?._id;
      if (uid) setPkgVisitCount(uid, 0);
      setItems((prev) => prev.filter((x) => x.id !== it.id));
      setOpen(false);
      onOpenDashboard?.("packages");
      return;
    }
    const key = it.type === "rejected" ? REJECT_DISMISS_KEY : PAY_DISMISS_KEY;
    const seen = loadSeen(key);
    seen.add(it.id);
    saveSeen(key, seen);
    setItems((prev) => prev.filter((x) => x.id !== it.id));
    setOpen(false);
    onOpenDashboard?.();
  };

  if (!user || user.role !== "customer") return null;

  const urgentCount = items.filter((i) => i.urgent).length;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`relative w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
          urgentCount > 0
            ? "text-red-300 hover:text-red-200 hover:bg-red-500/10"
            : "text-apex-cream/80 hover:text-apex-gold hover:bg-apex-card"
        }`}
        data-testid="notification-bell"
        aria-label="Notifikace"
      >
        <Bell className={`w-5 h-5 ${urgentCount > 0 ? "animate-pulse" : ""}`} />
        {items.length > 0 && (
          <span
            className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center animate-pulse ${
              urgentCount > 0 ? "bg-red-500 text-white" : "bg-apex-gold text-apex-bg"
            }`}
            data-testid="notification-badge"
          >
            {items.length}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.97 }}
            transition={{ duration: 0.18 }}
            className="absolute right-0 mt-3 w-80 max-w-[90vw] rounded-2xl bg-apex-surface border border-apex-line shadow-2xl overflow-hidden z-50"
            data-testid="notification-dropdown"
          >
            <div className="px-4 py-3 border-b border-apex-line flex items-center justify-between">
              <span className="text-sm font-semibold text-apex-cream">Upozornění</span>
              {items.length > 0 && <span className="text-xs text-apex-gold">{items.length} čeká na akci</span>}
            </div>
            {items.length === 0 ? (
              <div className="px-4 py-6 text-sm text-apex-cream/60 text-center" data-testid="notification-empty">
                Nemáte žádná upozornění.
              </div>
            ) : (
              <div className="max-h-80 overflow-y-auto">
                {items.map((it) => {
                  const b = it.booking;
                  if (it.type === "package_offer") {
                    return (
                      <button
                        key="package-offer"
                        onClick={() => dismissOnOpen(it)}
                        className="w-full text-left px-4 py-3.5 hover:bg-apex-gold/8 transition-colors border-b border-apex-line/50 last:border-b-0 flex items-start gap-3"
                        style={{ background: "linear-gradient(90deg, rgba(181,117,75,0.10) 0%, rgba(181,117,75,0.0) 100%)" }}
                        data-testid="notification-package-offer"
                      >
                        <span
                          className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 text-white"
                          style={{ background: "#B5754B" }}
                        >
                          <Package className="w-4 h-4" />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm text-apex-cream font-semibold">
                            🎁 Speciální nabídka — výhodné balíčky
                          </span>
                          <span className="block text-xs text-apex-cream/65 mt-0.5">
                            Sestavili jsme 4 balíčky pro pravidelnou péči o vaše auto.
                          </span>
                          <span className="inline-flex items-center gap-1 text-xs mt-1.5 font-semibold" style={{ color: "#B5754B" }}>
                            Prohlédnout balíčky <ChevronRight className="w-3 h-3" />
                          </span>
                        </span>
                      </button>
                    );
                  }
                  if (it.type === "pay") {
                    return (
                      <button
                        key={`pay-${it.id}`}
                        onClick={() => dismissOnOpen(it)}
                        className={`w-full text-left px-4 py-3.5 transition-colors border-b border-apex-line/50 last:border-b-0 flex items-start gap-3 ${
                          it.urgent ? "bg-red-500/8 hover:bg-red-500/15" : "hover:bg-apex-card"
                        }`}
                        data-testid={`notification-item-${it.id}`}
                      >
                        <span className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          it.urgent ? "bg-red-500/20 text-red-300" : "bg-apex-gold/15 text-apex-gold"
                        }`}>
                          {it.urgent ? <AlertTriangle className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />}
                        </span>
                        <span className="flex-1 min-w-0">
                          {it.urgent ? (
                            <span className="block text-sm text-red-200 font-semibold" data-testid={`notification-urgent-${it.id}`}>
                              ⚠️ Zítra je termín — zaplaťte prosím dnes
                            </span>
                          ) : (
                            <span className="block text-sm text-apex-cream font-semibold">Rezervace čeká na platbu</span>
                          )}
                          <span className="block text-xs text-apex-cream/60 mt-0.5 truncate">
                            {b.date} · {b.time} · {Math.round(b.totalPrice).toLocaleString("cs-CZ")} Kč
                          </span>
                          <span className={`inline-flex items-center gap-1 text-xs mt-1.5 font-semibold ${it.urgent ? "text-red-300" : "text-apex-gold"}`}>
                            Zaplatit v Mé rezervace <ChevronRight className="w-3 h-3" />
                          </span>
                        </span>
                      </button>
                    );
                  }
                  // rejected
                  return (
                    <div
                      key={`rej-${it.id}`}
                      className="w-full px-4 py-3.5 border-b border-apex-line/50 last:border-b-0 flex items-start gap-3 bg-red-500/5"
                      data-testid={`notification-rejected-${it.id}`}
                    >
                      <span className="w-9 h-9 rounded-xl bg-red-500/15 text-red-300 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <XCircle className="w-4 h-4" />
                      </span>
                      <button
                        type="button"
                        onClick={() => dismissOnOpen(it)}
                        className="flex-1 min-w-0 text-left"
                      >
                        <span className="block text-sm text-apex-cream font-semibold">Rezervace byla zamítnuta</span>
                        <span className="block text-xs text-apex-cream/60 mt-0.5 truncate">
                          {b.date} · {b.time}
                        </span>
                        {b.proposedDate && (
                          <span className="inline-flex items-center gap-1 text-xs text-apex-gold mt-1.5">
                            <AlarmClock className="w-3 h-3" /> Navrhujeme: {b.proposedDate}{b.proposedTime ? ` v ${b.proposedTime}` : ""}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1 text-xs text-apex-cream/70 mt-1.5 font-semibold underline-offset-2 hover:underline">
                          Otevřít detail <ChevronRight className="w-3 h-3" />
                        </span>
                      </button>
                      <button
                        onClick={(e) => dismissRejected(it.id, e)}
                        className="text-apex-cream/40 hover:text-apex-cream text-[10px] uppercase tracking-wider self-start"
                        data-testid={`notification-dismiss-${it.id}`}
                        aria-label="Skrýt upozornění"
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
